import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  Moon,
  Zap,
  Coffee,
  Clock,
  ArrowRight,
  X,
} from "lucide-react";

const STATUS_STYLES = {
  protected: {
    border: "border-emerald-500/40",
    bg: "bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent",
    icon: ShieldCheck,
    iconColor: "text-emerald-300",
    chipBg: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    label: "Protected",
  },
  watch: {
    border: "border-amber-500/40",
    bg: "bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent",
    icon: ShieldAlert,
    iconColor: "text-amber-300",
    chipBg: "bg-amber-500/15 text-amber-300 border-amber-500/40",
    label: "Watch",
  },
  overloaded: {
    border: "border-red-500/40",
    bg: "bg-gradient-to-r from-red-500/10 via-red-500/5 to-transparent",
    icon: AlertTriangle,
    iconColor: "text-red-300",
    chipBg: "bg-red-500/15 text-red-300 border-red-500/40",
    label: "Overloaded",
  },
  no_data: {
    border: "border-zinc-700",
    bg: "bg-zinc-900/50",
    icon: Moon,
    iconColor: "text-zinc-400",
    chipBg: "bg-zinc-500/15 text-zinc-300 border-zinc-500/40",
    label: "No data",
  },
};

const SUGGESTION_ICON = {
  reschedule_meeting: Clock,
  defer_task: ArrowRight,
  recovery_habit: Coffee,
  momentum: Zap,
};

export default function TomorrowLookAhead() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const key = `niko_lookahead_dismissed_${new Date().toISOString().slice(0, 10)}`;
    if (sessionStorage.getItem(key) === "1") setDismissed(true);

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/analytics/look-ahead`, { withCredentials: true });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dismiss = () => {
    const key = `niko_lookahead_dismissed_${new Date().toISOString().slice(0, 10)}`;
    sessionStorage.setItem(key, "1");
    setDismissed(true);
  };

  if (loading || !data || dismissed) return null;

  const style = STATUS_STYLES[data.status] || STATUS_STYLES.no_data;
  const Icon = style.icon;

  return (
    <Card
      className={`bento-card ${style.border} ${style.bg}`}
      data-testid={`lookahead-${data.status}`}
    >
      <CardContent className="pt-4 pb-4">
        <div className="flex items-start gap-4">
          <div className={`p-2 rounded-lg ${style.chipBg.replace("text-", "").replace(" border-", " ").replace(/\/\d+/g, (s) => s.replace("/", "/"))} flex-shrink-0`}>
            <Icon className={`w-5 h-5 ${style.iconColor}`} />
          </div>

          <div className="flex-1 min-w-0 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded border ${style.chipBg}`}>
                    {style.label}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Tomorrow Look-Ahead</span>
                </div>
                <div className="font-semibold text-white text-base leading-snug" data-testid="lookahead-headline">
                  {data.headline}
                </div>
                {data.rationale && (
                  <div className="text-xs text-zinc-400 mt-1 leading-relaxed" data-testid="lookahead-rationale">
                    {data.rationale}
                  </div>
                )}
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={dismiss}
                className="h-7 w-7 text-zinc-500 hover:text-white -mr-1 -mt-1 flex-shrink-0"
                data-testid="lookahead-dismiss"
                title="Dismiss for today"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {data.suggestions?.length > 0 && (
              <ul className="space-y-1.5" data-testid="lookahead-suggestions">
                {data.suggestions.map((s, i) => {
                  const SIcon = SUGGESTION_ICON[s.type] || Zap;
                  return (
                    <li key={`sug-${i}`} className="flex items-start gap-2 text-sm">
                      <SIcon className="w-4 h-4 text-zinc-300 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <div className="text-white font-medium">{s.action}</div>
                        <div className="text-xs text-zinc-400">{s.reason}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
